import { useState, type FormEvent } from 'react'
import { api } from '@/api/client'
import { useFetch, useMutate } from '@/hooks/useApi'
import { useConfigQuery } from '@/context/ClubContext'
import {
  AsyncState,
  Button,
  ConfirmDialog,
  Field,
  Icon,
  IconButton,
  Input,
  Modal,
  PageHead,
  Select
} from '@/components/ui'
import { ClubGate } from '@/components/ClubGate'
import { type PinUser, type PinRole, PIN_ROLE_LABEL } from '@/lib/config'

const ROLES: PinRole[] = ['owner', 'admin', 'operator']

export function Users(): React.JSX.Element {
  return (
    <>
      <PageHead
        eyebrow="Конфигурация"
        title="Пользователи"
        lead="PIN-пользователи терминала: владелец, администратор и операторы. PIN — это код входа в кассовое приложение клуба."
      />
      <ClubGate>
        <UsersTable />
      </ClubGate>
    </>
  )
}

function UsersTable(): React.JSX.Element {
  const query = useConfigQuery()
  const list = useFetch<PinUser[]>('/v1/users', query, [query?.clubId])
  const [editing, setEditing] = useState<PinUser | 'new' | null>(null)
  const [deleting, setDeleting] = useState<PinUser | null>(null)
  const del = useMutate()

  const rows = list.data ?? []

  // Ключи «имя|роль», встречающиеся больше одного раза, — кандидаты в дубли.
  const duplicateKeys = new Set(
    rows.map((u) => `${u.name}|${u.role}`).filter((key, i, all) => all.indexOf(key) !== i)
  )

  const onDeleteConfirm = async (): Promise<void> => {
    if (!deleting) return
    const ok = await del.mutate(() => api.del(`/v1/users/${deleting.id}`, { query }))
    if (ok) {
      setDeleting(null)
      list.reload()
    }
  }

  return (
    <>
      <div className="toolbar">
        <div className="toolbar__left">
          <span className="muted">{rows.length} пользователь(-ей)</span>
        </div>
        <div className="toolbar__right">
          <Button variant="primary" size="sm" onClick={() => setEditing('new')}>
            {Icon.plus}
            Новый пользователь
          </Button>
        </div>
      </div>

      <AsyncState query={list} isEmpty={rows.length === 0} empty="Пользователи ещё не заданы." />

      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Имя</th>
                <th>Роль</th>
                <th>Терминал</th>
                <th style={{ width: 96 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.id}>
                  <td>
                    {u.name}
                    {/* Одинаковые имя+роль с разных терминалов — почти всегда клон
                        стандартной учётки после переустановки. Помечаем, чтобы было
                        понятно, что лишнюю можно удалить. */}
                    {duplicateKeys.has(`${u.name}|${u.role}`) && (
                      <span
                        className="muted"
                        style={{ marginLeft: 8, fontSize: 11.5, color: 'var(--amber)' }}
                        title="Есть ещё одна учётка с таким именем и ролью — вероятно, дубль после переустановки терминала"
                      >
                        возможно дубль
                      </span>
                    )}
                  </td>
                  <td>{PIN_ROLE_LABEL[u.role]}</td>
                  <td title={u.deviceId ?? 'создана в бекофисе'}>
                    {u.deviceId ? (
                      <code style={{ fontSize: 12 }}>{u.deviceId.slice(0, 8)}</code>
                    ) : (
                      <span className="muted">бекофис</span>
                    )}
                  </td>
                  <td>
                    <div className="row-actions">
                      <IconButton title="Изменить" onClick={() => setEditing(u)}>
                        {Icon.edit}
                      </IconButton>
                      <IconButton danger title="Удалить" onClick={() => setDeleting(u)}>
                        {Icon.trash}
                      </IconButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <UserForm
          user={editing === 'new' ? null : editing}
          query={query}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            list.reload()
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="Удалить пользователя"
          message={`Удалить пользователя «${deleting.name}»? Действие нельзя отменить.`}
          busy={del.saving}
          error={del.error}
          onConfirm={() => void onDeleteConfirm()}
          onCancel={() => setDeleting(null)}
        />
      )}
    </>
  )
}

function UserForm({
  user,
  query,
  onClose,
  onSaved
}: {
  user: PinUser | null
  query: ReturnType<typeof useConfigQuery>
  onClose: () => void
  onSaved: () => void
}): React.JSX.Element {
  const isEdit = user !== null
  const [name, setName] = useState(user?.name ?? '')
  const [role, setRole] = useState<PinRole>(user?.role ?? 'operator')
  const [pin, setPin] = useState('')
  const [validation, setValidation] = useState<string | null>(null)
  const save = useMutate()

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) return setValidation('Укажите имя пользователя.')
    // PIN обязателен при создании; при редактировании — только если меняют.
    if (!isEdit && !/^\d{4,8}$/.test(pin)) return setValidation('PIN — 4–8 цифр.')
    if (isEdit && pin && !/^\d{4,8}$/.test(pin)) return setValidation('PIN — 4–8 цифр.')
    setValidation(null)

    // При редактировании отправляем PIN только если его задали (иначе не меняем).
    const body: Record<string, unknown> = { name: trimmed, role }
    if (pin) body.pin = pin

    const ok = await save.mutate(() =>
      isEdit
        ? api.patch(`/v1/users/${user.id}`, body, { query })
        : api.post('/v1/users', { ...body, pin }, { query })
    )
    if (ok) onSaved()
  }

  return (
    <Modal
      title={isEdit ? 'Изменить пользователя' : 'Новый пользователь'}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button" disabled={save.saving}>
            Отмена
          </Button>
          <Button variant="primary" busy={save.saving} type="submit" form="user-form">
            Сохранить
          </Button>
        </>
      }
    >
      <form id="user-form" onSubmit={(e) => void onSubmit(e)}>
        <div className="form-grid">
          <Field label="Имя" htmlFor="u-name" full>
            <Input
              id="u-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="напр. Иван Петров"
              autoFocus
            />
          </Field>
          <Field label="Роль" htmlFor="u-role">
            <Select id="u-role" value={role} onChange={(e) => setRole(e.target.value as PinRole)}>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {PIN_ROLE_LABEL[r]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="PIN" htmlFor="u-pin">
            <Input
              id="u-pin"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              placeholder={isEdit ? 'не менять' : '4–8 цифр'}
            />
            {isEdit && <span className="field__hint">Оставьте пустым, чтобы не менять PIN.</span>}
          </Field>
        </div>

        {validation && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {validation}
          </div>
        )}
        {save.error && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {save.error}
          </div>
        )}
      </form>
    </Modal>
  )
}
